
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  GoogleMap,
  LoadScript,
  MarkerF,
  InfoWindowF,
  StandaloneSearchBox,
} from "@react-google-maps/api";
import styles from "./style.module.css";
import ClockIcon from 'assets/SVG/ClockIcon';
import PhoneSvg from "../../assets/SVG/PhoneSvg";

const defaultCenter = {
  lat: 40.150011,
  lng: 44.495715,
};

const libraries = ["places"];
const markerIcon = require("./assets/mapMarker.png");

const Map = React.memo(
  ({
    addresses = [],
    mapCenter,
    setMapCenter,
    selectedIndex,
    setSelected,
    selected,
    handleMarkerClick,
    disableMap,
    input,
    setAddressName,
    fitAllMarkers = false,
    focusKey = 0,
  }) => {

    const searchBoxRef = useRef();
    const mapRef = useRef(null);
    const fittedKeyRef = useRef("");
    const nativeMarkersRef = useRef([]);
    const { currentLanguage } = useSelector(({ main }) => main);
    const [markerPosition, setMarkerPosition] = useState();
    const [countAddress, setCountAddress] = useState(0);
    const [mapReady, setMapReady] = useState(false);
    // Freeze initial center for fitAllMarkers so React doesn't fight fitBounds / drag
    const [frozenCenter] = useState(() => ({
      lat: parseFloat(mapCenter?.lat) || defaultCenter.lat,
      lng: parseFloat(mapCenter?.lng) || defaultCenter.lng,
    }));

    const mapContainerStyle = {
      display: disableMap ? "none" : "block",
      width: "100%",
      height: "100%",
      outline: "none",
    };

    const validMapCenter = {
      lat: parseFloat(mapCenter?.lat) || defaultCenter.lat,
      lng: parseFloat(mapCenter?.lng) || defaultCenter.lng,
    };

    const addressesKey = (addresses || [])
      .map((a) => `${a?.id}:${a?.location?.lat},${a?.location?.lng}`)
      .join("|");

    const clearNativeMarkers = useCallback(() => {
      nativeMarkersRef.current.forEach((marker) => {
        window.google?.maps?.event?.clearInstanceListeners?.(marker);
        marker.setMap(null);
      });
      nativeMarkersRef.current = [];
    }, []);

    const fitMapToAddresses = useCallback((mapInstance, force = false) => {
      const map = mapInstance || mapRef.current;
      if (!fitAllMarkers || !map || !window.google?.maps || !addresses?.length) {
        return;
      }

      if (!force && fittedKeyRef.current === addressesKey) {
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      let hasPoint = false;

      addresses.forEach((address) => {
        const lat = parseFloat(address?.location?.lat);
        const lng = parseFloat(address?.location?.lng);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          bounds.extend({ lat, lng });
          hasPoint = true;
        }
      });

      if (!hasPoint) return;

      fittedKeyRef.current = addressesKey;
      map.fitBounds(bounds, 72);

      window.google.maps.event.addListenerOnce(map, "idle", () => {
        const zoom = map.getZoom();
        if (typeof zoom === "number" && zoom > 13) {
          map.setZoom(13);
        }
      });
    }, [addresses, addressesKey, fitAllMarkers]);

    const syncNativeMarkers = useCallback(() => {
      const map = mapRef.current;
      if (!fitAllMarkers || !map || !window.google?.maps || !mapReady) return;

      clearNativeMarkers();

      addresses.forEach((address, index) => {
        const lat = parseFloat(address?.location?.lat) || defaultCenter.lat;
        const lng = parseFloat(address?.location?.lng) || defaultCenter.lng;
        const marker = new window.google.maps.Marker({
          map,
          position: { lat, lng },
          icon: markerIcon,
          optimized: false,
        });
        marker.addListener("click", () => {
          handleMarkerClick?.(address, index);
        });
        nativeMarkersRef.current.push(marker);
      });
    }, [addresses, clearNativeMarkers, fitAllMarkers, handleMarkerClick, mapReady]);

    const getAddressFromCoordinates = async (markerPosition) => {
      try {
        const positions = {
          lat:
            typeof markerPosition?.lat == "function"
              ? markerPosition?.lat()
              : markerPosition?.lat,
          lng:
            typeof markerPosition?.lng == "function"
              ? markerPosition?.lng()
              : markerPosition?.lng,
        };
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${positions.lat},${positions.lng}&key=AIzaSyCPnJK15brSbMXRXL-uhdtyb0ZKNizR_0I`
        );
        const data = await response.json();
        if (data.status === "OK") {
          !!countAddress && setAddressName(data.results[0].formatted_address);
          setCountAddress((prev) => prev + 1);
        }
        else {
          console.error("Error fetching address:", data);
        }
      }
      catch (error) {
        console.error("Error fetching address:", error);
      }
    };

    const onPlacesChanged = () => {
      const places = searchBoxRef.current.state.searchBox?.getPlaces();
      if (places.length > 0) {
        const { geometry } = places[0];
        const { location } = geometry;
        const latLng = { lat: location.lat(), lng: location.lng() };
        if (!disableMap) {
          setMapCenter(latLng);
          setMarkerPosition(latLng);
        }
        else {
          getAddressFromCoordinates(latLng);
        }
      }
    };

    const handleMarkerDragEnd = (mapProps) => {
      setMarkerPosition(mapProps.latLng);
    };

    const handleClick = (mapProps) => {
      setMarkerPosition(mapProps.latLng);
    };

    useLayoutEffect(() => {
      if (fitAllMarkers) return;
      if (!Object.keys(markerPosition || {}).length)
        setTimeout(() => {
          setMarkerPosition(defaultCenter);
        }, 1000);
      if (Object.keys(markerPosition || {})?.length)
          getAddressFromCoordinates(markerPosition);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markerPosition?.lat, fitAllMarkers]);

    useEffect(() => {
      if (fitAllMarkers && mapReady) {
        syncNativeMarkers();
        fitMapToAddresses(null, fittedKeyRef.current !== addressesKey);
      }
      return () => {
        if (fitAllMarkers) clearNativeMarkers();
      };
    }, [addressesKey, clearNativeMarkers, fitAllMarkers, fitMapToAddresses, mapReady, syncNativeMarkers]);

    useEffect(() => {
      // focusKey > 0 means user clicked a card/marker; keep initial fitBounds overview otherwise
      if (!fitAllMarkers || !mapRef.current || selectedIndex == null || !mapReady || !focusKey) {
        return;
      }
      const address = addresses?.[selectedIndex];
      const lat = parseFloat(address?.location?.lat);
      const lng = parseFloat(address?.location?.lng);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return;

      const map = mapRef.current;
      map.panTo({ lat, lng });
      map.setZoom(16);
    }, [focusKey, selectedIndex, addresses, fitAllMarkers, mapReady]);

    useEffect(() => () => clearNativeMarkers(), [clearNativeMarkers]);

    return (
      <div
        className={styles.mapShell}
        style = {{
          height: disableMap ? "auto" : "460px",
          width: "100%",
        }}
      >
        <LoadScript
          googleMapsApiKey = { process.env.REACT_APP_GOOGLE_MAP }
          libraries = { libraries }
        >
          {disableMap ? (
            <StandaloneSearchBox
              ref = { searchBoxRef }
              onPlacesChanged = { onPlacesChanged }
            >
              <div>{ input }</div>
            </StandaloneSearchBox>
          ) : (
            <>
              <StandaloneSearchBox
                ref = { searchBoxRef }
                onPlacesChanged = { onPlacesChanged }
              >
                <div>{ input }</div>
              </StandaloneSearchBox>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                mapContainerClassName={styles.mapCanvas}
                {...(fitAllMarkers
                  ? {
                      defaultCenter: frozenCenter,
                      defaultZoom: 11,
                    }
                  : {
                      center: validMapCenter,
                      zoom: 14,
                    })}
                onLoad={(map) => {
                  mapRef.current = map;
                  setMapReady(true);
                  if (fitAllMarkers) {
                    fittedKeyRef.current = "";
                    fitMapToAddresses(map, true);
                  }
                  const div = map.getDiv?.();
                  if (div) {
                    div.setAttribute("tabindex", "-1");
                    div.style.outline = "none";
                  }
                }}
                onUnmount={() => {
                  clearNativeMarkers();
                  mapRef.current = null;
                  setMapReady(false);
                }}
                onClick={fitAllMarkers ? (e) => {
                  const div = mapRef.current?.getDiv?.();
                  div?.blur?.();
                  e?.domEvent?.target?.blur?.();
                } : handleClick}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: true,
                  gestureHandling: "greedy",
                  clickableIcons: false,
                  keyboardShortcuts: false,
                  draggable: true,
                  disableDoubleClickZoom: false,
                }}
              >
                {!fitAllMarkers &&
                  addresses?.length > 0 &&
                  addresses.map((address, index) => (
                    <MarkerF
                      key={address.id || index}
                      position={{
                        lat:
                          parseFloat(address.location?.lat) ||
                          defaultCenter.lat,
                        lng:
                          parseFloat(address.location?.lng) ||
                          defaultCenter.lng,
                      }}
                      icon={markerIcon}
                      onClick={() => handleMarkerClick(address, index)}
                    />
                  ))
                }
                {!fitAllMarkers && markerPosition && (
                  <MarkerF
                    position = { markerPosition }
                    draggable = { true }
                    onDragEnd = { handleMarkerDragEnd }
                    icon = { markerIcon }
                  />
                )}
                { selected && selectedIndex !== null ? (
                  <InfoWindowF
                    position = { selected }
                    onCloseClick = { () => setSelected(null) }
                  >
                    <div className = { styles.addr_cont_inf }>
                      <span className = { styles.locname_ch }>
                        { addresses[selectedIndex]?.["address_" + currentLanguage] }
                      </span>
                      <img
                        src = { process.env.REACT_APP_STORAGE_URL + addresses[selectedIndex]?.["image_" + currentLanguage] }
                        alt = { addresses[selectedIndex]?.["address_" + currentLanguage] || "branch" }
                        className={styles.branchImage}
                      />
                      <div className={styles.time_phone}>
                        <div className={styles.chosen_phone_mail_user}>
                          <ClockIcon/>
                          { addresses[selectedIndex]?.["work_time_" + currentLanguage] }
                        </div>
                        <a
                          href={`tel:${String(addresses[selectedIndex]?.["tel_" + currentLanguage] || "").replace(/\s+/g, "")}`}
                          className={styles.chosen_phone_mail_user}
                        >
                          <PhoneSvg width={12} height={12} />
                          { addresses[selectedIndex]?.["tel_" + currentLanguage] }
                        </a>
                      </div>
                    </div>
                  </InfoWindowF>
                )
                :
                null
                }
              </GoogleMap>
            </>
          )}
        </LoadScript>
      </div>
    );
  }
);

export default Map;
